import React from "react";
import { DatePicker } from "../../../../../../../react-core/Datepicker/DatePicker";
import { CommonHelper } from "../../../../../../../react-core/Helpers/CommonHelper";
import { Lightbox } from "../../../../../../../react-core/Html/Components/Lightbox";
import { FormComponent } from "../../../../../../../react-core/Html/FormComponent";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { IJsonDate, JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { AccountRoleActorCategory } from "../../../../../../../Wrap/Constants/Enums/AccountRoleActorCategory";
import { RelationshipType } from "../../../../../../../Wrap/Constants/Enums/Customer/RelationshipType";
import { TrustAuthorisationRequestType } from "../../../../../../../Wrap/Constants/Enums/Customer/TrustAuthorisationRequestType";
import { TrustRemovalReason } from "../../../../../../../Wrap/Constants/Enums/Customer/TrustRemovalReason";
import { Organisation } from "../../../../../../../Wrap/Models/Models/Customer/Organisation";
import { OrganisationMember } from "../../../../../../../Wrap/Models/Models/Customer/OrganisationMember";
import { CheckboxGroup } from "../../../../../../Shared/CheckboxGroup/CheckboxGroup";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { ACCEPT_DATE_FORMATS, ContentDatePicker } from "../../../../../../Shared/Entities/Constants/DateConstants";
import { TitleMap } from "../../../../../../Shared/Entities/Maps/TitleMap";
import { OrganisationMemberPopUpInfo, RelationshipTypeMap } from "../../../AddClient/Resource/Contents";
import { AddTrustMemberAction } from "../../AddTrustMember/Action/AddTrustMemberAction";
import { RemoveReasonTypeMap } from "../../Resource/Contents";
import { ShareOfTrustTable } from "./ShareOfTrustTable";
import { checkFinalBeneficiary, checkTrusteeIsSmallerThanSettlor } from "./TrustMemberHelper";

interface IRemoveMemberLightboxProps {
    organisation: Organisation;
    editItem: OrganisationMember;
    organisationMembers: Array<OrganisationMember>;
    trustCategory: string;
    onClose: () => void;
    accountId: number;
}

interface IRemoveMemberLightboxState {
    relationshipsRemoved: Array<RelationshipType>;
    relationshipsSaved: boolean;
    relationshipsUpdatedAfterSave: Array<RelationshipType>;
    removeReason: TrustRemovalReason;
    dateOfDeath: IJsonDate;
    showTrusteesMoreThanSettlorsWarning: boolean;
    showFlexibleBeneficiaryWarning: boolean;
    updatedOrganisationModel: Organisation;
    showShareOfTrustWarning: boolean;
    showRemoveTrusteeWarning: boolean;
}

export class RemoveMemberLightbox extends FormComponent<IRemoveMemberLightboxProps, IRemoveMemberLightboxState> {
    constructor(props: IRemoveMemberLightboxProps) {
        super(props);
        this.state = {
            relationshipsRemoved: [],
            relationshipsSaved: false,
            relationshipsUpdatedAfterSave: [],
            removeReason: null,
            dateOfDeath: null,
            showTrusteesMoreThanSettlorsWarning: false,
            showFlexibleBeneficiaryWarning: false,
            updatedOrganisationModel: null,
            showShareOfTrustWarning: false,
            showRemoveTrusteeWarning: false,
        };
    }

    componentDidMount() {
        const { organisation } = this.props;
        const organisationData = JSON.parse(JSON.stringify(organisation));
        organisationData.OrganisationMembers = [];
        organisationData.Beneficiaries = [];
        organisationData.ShareOfTrustItem = [];
        this.setState({ updatedOrganisationModel: organisationData });
        if (
            checkFinalBeneficiary(this.props.organisationMembers) &&
            this.props.trustCategory == "Flexible" &&
            this.props.editItem?.Relationships?.includes(RelationshipType.Beneficiary)
        ) {
            this.setState({ showFlexibleBeneficiaryWarning: true });
        }
    }

    private onSubmit() {
        const { editItem, organisation } = this.props;
        const { removeReason, relationshipsUpdatedAfterSave } = this.state;
        if (
            relationshipsUpdatedAfterSave.includes(RelationshipType.IndividualTrustee) ||
            relationshipsUpdatedAfterSave.includes(RelationshipType.CorporateTrustee)
        ) {
            AddTrustMemberAction.postTrustAuthorisation(
                editItem?.CustomerId,
                organisation?.CustomerId,
                TrustAuthorisationRequestType.RemoveAsTrustee,
                removeReason,
                () => {
                    this.deleteNonTrusteeRoles();
                }
            );
        } else {
            this.deleteNonTrusteeRoles();
        }
    }

    private deleteNonTrusteeRoles() {
        const { editItem } = this.props;
        const { removeReason, relationshipsUpdatedAfterSave, dateOfDeath, updatedOrganisationModel } = this.state;
        const nonTrusteeRolesToDelete = relationshipsUpdatedAfterSave.filter(
            (x) => x != RelationshipType.IndividualTrustee && x != RelationshipType.CorporateTrustee
        );
        if (nonTrusteeRolesToDelete.length > 0) {
            if (editItem?.Individual?.ActorCategory == AccountRoleActorCategory.Associate) {
                updatedOrganisationModel.Beneficiaries = [editItem];
                updatedOrganisationModel.Beneficiaries[0].Relationships = nonTrusteeRolesToDelete;
            } else {
                updatedOrganisationModel.OrganisationMembers = [editItem];
                updatedOrganisationModel.OrganisationMembers[0].Relationships = nonTrusteeRolesToDelete;
            }
            AddTrustMemberAction.removeTrustMember(updatedOrganisationModel, removeReason, dateOfDeath, () => {
                this.props.onClose();
            });
        } else {
            this.props.onClose();
        }
    }

    private getRemovalReasons() {
        const { trustCategory, organisationMembers, editItem } = this.props;
        const { relationshipsRemoved } = this.state;
        if (
            relationshipsRemoved.includes(RelationshipType.Beneficiary) &&
            trustCategory == "Flexible" &&
            checkFinalBeneficiary(organisationMembers)
        ) {
            return [];
        }
        if (relationshipsRemoved.includes(RelationshipType.Beneficiary) && trustCategory == "Absolute") {
            return [TrustRemovalReason.Death];
        } else if (relationshipsRemoved.includes(RelationshipType.Beneficiary)) {
            if (editItem?.Individual?.ActorCategory == AccountRoleActorCategory.Associate) {
                return [TrustRemovalReason.Death, TrustRemovalReason.Removed];
            } else {
                return [TrustRemovalReason.Removed];
            }
        } else {
            return [TrustRemovalReason.Removed, TrustRemovalReason.Resignation, TrustRemovalReason.Retirement];
        }
    }

    private renderBeneficiariesTable() {
        const { editItem, organisationMembers, accountId } = this.props;
        const { updatedOrganisationModel } = this.state;
        return (
            <>
                <ShareOfTrustTable
                    data={null}
                    onRef={(ref) => {}}
                    organisation={updatedOrganisationModel}
                    deletedBeneficiary={editItem}
                    remainingBeneficiaries={organisationMembers.filter(
                        (x) =>
                            x?.Relationships.includes(RelationshipType.Beneficiary) &&
                            x?.CustomerId != editItem?.CustomerId
                    )}
                    isAddBeneficiary={false}
                    IsAddAdditionalBeneficiary={false}
                    onChange={(organisation) => this.setState({ updatedOrganisationModel: organisation })}
                    accountId={accountId}
                />
            </>
        );
    }

    private renderRemovalReason() {
        const { editItem, organisationMembers, trustCategory } = this.props;
        const { relationshipsRemoved, removeReason, relationshipsUpdatedAfterSave } = this.state;
        return (
            <>
                <span>If you are removing an assigned role of a trust member, then a reason must be given.</span>
                <br />
                <SelectEditor
                    options={this.getRemovalReasons()}
                    getText={(v) => {
                        return RemoveReasonTypeMap[v];
                    }}
                    cssWrapper="control-size-300"
                    placeholder="Please select"
                    label="Remove reason"
                    selected={this.state.removeReason}
                    onChange={(v) => {
                        let updatedRelationships = relationshipsRemoved;
                        // All roles should be removed when reason selected is removed or death,
                        // with the exception of scenario: trustee+ settlor + removed => trustee role (not valid reason for Settlor)
                        if (v == TrustRemovalReason.Death || v == TrustRemovalReason.Removed) {
                            if (
                                !(
                                    editItem?.Relationships.includes(RelationshipType.Settlor) &&
                                    v == TrustRemovalReason.Removed
                                )
                            ) {
                                updatedRelationships = editItem?.Relationships;
                            }
                        }
                        if (updatedRelationships.includes(RelationshipType.IndividualTrustee)) {
                            const showWarning = this.checkTrusteesGreaterThanSettlors(updatedRelationships);
                            this.setState({
                                showTrusteesMoreThanSettlorsWarning: showWarning,
                            });
                        }

                        this.setState({ relationshipsUpdatedAfterSave: updatedRelationships, removeReason: v });
                    }}
                    required={"Please select"}
                />

                {removeReason == TrustRemovalReason.Death && (
                    <DatePicker
                        selected={JsonDateParser.toDateNoOffset(this.state.dateOfDeath)}
                        label={"Date of death"}
                        onChange={(v) => {
                            this.setState({ dateOfDeath: JsonDateParser.toJsonDateNoOffset(v) });
                        }}
                        showYearDropdown={true}
                        todayButton={ContentDatePicker.Today}
                        isClearable={true}
                        atMost={{ value: new Date(), errorMessage: "Date can not be in the future" }}
                        dateFormat={ContentDatePicker.DateFormat}
                        acceptDateFormats={ACCEPT_DATE_FORMATS}
                        formGroupCssWrapper="control-size-300"
                        required="Please enter"
                    />
                )}

                {relationshipsUpdatedAfterSave.includes(RelationshipType.Beneficiary) &&
                    this.renderBeneficiariesTable()}

                <br />
                <button className="btn secondary mr-10" onClick={() => this.props.onClose()}>
                    Cancel
                </button>
                <button
                    className="btn primary"
                    disabled={
                        (removeReason == TrustRemovalReason.Death || removeReason == TrustRemovalReason.Removed) &&
                        editItem?.Relationships.includes(RelationshipType.Beneficiary) &&
                        checkFinalBeneficiary(organisationMembers) &&
                        trustCategory == "Flexible"
                    }
                    onClick={() => {
                        if (
                            this.isValid() &&
                            (!relationshipsUpdatedAfterSave.includes(RelationshipType.Beneficiary) ||
                                this.checkShareOfTrustSum())
                        ) {
                            this.onSubmit();
                        }
                    }}
                >
                    Save
                </button>
            </>
        );
    }

    private checkShareOfTrustSum() {
        const { updatedOrganisationModel } = this.state;
        let sum = 0;

        if (!CommonHelper.isNullOrEmpty(updatedOrganisationModel?.ShareOfTrustItem)) {
            updatedOrganisationModel?.ShareOfTrustItem.forEach((x) => {
                const share = x?.Percentage ? Number(x?.Percentage) : 0;
                sum = sum + share;
            });
        }
        if (Math.abs(100 - sum) > 0.1 && updatedOrganisationModel?.ShareOfTrustItem?.length > 0) {
            this.setState({ showShareOfTrustWarning: true });
            return false;
        }
        return true;
    }

    private checkTrusteesGreaterThanSettlors(deletedRelatioships: Array<RelationshipType>) {
        const remainingRoles = this.props.editItem.Relationships.filter((x) => !deletedRelatioships.includes(x));
        return checkTrusteeIsSmallerThanSettlor(this.props.organisationMembers, remainingRoles);
    }

    private validateRoleSelected() {
        const { editItem } = this.props;
        const { relationshipsRemoved } = this.state;
        if (
            relationshipsRemoved.includes(RelationshipType.IndividualTrustee) &&
            relationshipsRemoved.length == 1 &&
            editItem?.Relationships.includes(RelationshipType.Beneficiary)
        ) {
            this.setState({ showRemoveTrusteeWarning: true });
            return false;
        }
        return true;
    }

    render() {
        const { editItem, trustCategory, organisationMembers } = this.props;
        const {
            relationshipsRemoved,
            relationshipsSaved,
            relationshipsUpdatedAfterSave,
            showTrusteesMoreThanSettlorsWarning,
            showFlexibleBeneficiaryWarning,
            showShareOfTrustWarning,
            showRemoveTrusteeWarning,
        } = this.state;
        const options = [];
        editItem?.Relationships.forEach((relationship) => {
            options.push({
                label: RelationshipTypeMap[relationship],
                onChange: (v) => {
                    if (v) {
                        const updated = relationshipsRemoved.filter((x) => x != relationship);
                        if (
                            v == RelationshipType.IndividualTrustee &&
                            !this.checkTrusteesGreaterThanSettlors(updated)
                        ) {
                            this.setState({ showTrusteesMoreThanSettlorsWarning: false });
                        }
                        this.setState({ relationshipsRemoved: updated, showRemoveTrusteeWarning: false });
                    } else {
                        const updated = relationshipsRemoved.concat([relationship]);
                        if (v == RelationshipType.IndividualTrustee && this.checkTrusteesGreaterThanSettlors(updated)) {
                            this.setState({ showTrusteesMoreThanSettlorsWarning: true });
                        }
                        this.setState({ relationshipsRemoved: updated, showRemoveTrusteeWarning: false });
                    }
                },
                value: relationshipsSaved
                    ? !relationshipsUpdatedAfterSave.includes(relationship)
                    : !relationshipsRemoved.includes(relationship),
                disabled:
                    relationshipsSaved ||
                    (trustCategory == "Flexible" &&
                        relationship == RelationshipType.Beneficiary &&
                        checkFinalBeneficiary(organisationMembers)) ||
                    relationship == RelationshipType.Settlor,
            });
        });
        return (
            <>
                <Lightbox
                    cssWrapper="is-default-lightbox"
                    onClose={() => this.props.onClose()}
                    show={true}
                    title={"Trust member - Remove trust member"}
                >
                    {showShareOfTrustWarning && (
                        <Alert
                            message={OrganisationMemberPopUpInfo.shareOfTrustWarning}
                            alertType={AlertType.Error}
                        ></Alert>
                    )}

                    {showTrusteesMoreThanSettlorsWarning && (
                        <Alert
                            message={OrganisationMemberPopUpInfo.moreTrusteesThanSettlors}
                            alertType={AlertType.Warning}
                        ></Alert>
                    )}

                    {showFlexibleBeneficiaryWarning && (
                        <Alert
                            message={OrganisationMemberPopUpInfo.flexibleBeneficary}
                            alertType={AlertType.Warning}
                        ></Alert>
                    )}

                    {showRemoveTrusteeWarning && (
                        <Alert
                            message={OrganisationMemberPopUpInfo.removeTrusteeWarning}
                            alertType={AlertType.Warning}
                        ></Alert>
                    )}
                    <h3>Trust member</h3>

                    {editItem?.Relationships.includes(RelationshipType.CorporateTrustee) && (
                        <h4>{editItem?.Company?.CompanyName}</h4>
                    )}
                    {!editItem?.Relationships.includes(RelationshipType.CorporateTrustee) && (
                        <h4>
                            {TitleMap.getString(editItem?.Individual?.PersonalDetails?.Title) +
                                " " +
                                editItem?.Individual?.PersonalDetails?.GivenName +
                                " " +
                                editItem?.Individual?.PersonalDetails?.FamilyName}
                        </h4>
                    )}

                    <br />
                    <CheckboxGroup type="block" options={options} />
                    {relationshipsSaved == false && (
                        <>
                            <button className="btn secondary mr-10" onClick={() => this.props.onClose()}>
                                Cancel
                            </button>
                            <button
                                className="btn primary"
                                onClick={() => {
                                    if (this.validateRoleSelected()) {
                                        this.setState({
                                            relationshipsSaved: true,
                                            relationshipsUpdatedAfterSave: relationshipsRemoved,
                                        });
                                    }
                                }}
                                disabled={relationshipsRemoved.length == 0}
                            >
                                Save
                            </button>
                        </>
                    )}
                    {relationshipsSaved == true && this.renderRemovalReason()}
                </Lightbox>
            </>
        );
    }
}
